import {
  Alert,
  Button,
  Flex,
  Grid,
  IconButton,
  IconTrashLine,
  IconWarningSolid,
  Pill,
  RadioInput,
  RadioInputGroup,
  Spinner,
  Text,
  ToggleGroup,
} from "@instructure/ui";
import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { AIService } from "../../../../services/AIService";
import { LearningOutcomesService } from "../../../../services/LearningOutcomesService";
import { UserDetailContext } from "../../../../utils/Context";
import { HighlightTokens } from "../../../../utils/HighlightTokens";
import { RefreshMetricsContext } from "../../../../views/Outcomes";
import {
  ERROR,
  useNotification,
} from "../../../CustomHooks/NotificationProvider";
import { DialogYesNo } from "../../../DialogYesNo/DialogYesNo";
import { EffectiveTermSelect } from "../../../LearningOutcomes/EffectiveTermSelect";
import {
  getCurrentVersion,
  getFutureVersions,
  isArchived,
  pendingArchive,
  sortOutcomes,
} from "../../../LearningOutcomes/utils";
import { ClassSelect } from "../../../Selectors/ClassSelect";
import { getEarliestCanvasTerm } from "../../../TermManagement/utils";
import { OutcomesContext } from "../Review";
import { AiStatement, AiStatementButton } from "./AIStatement";
import OutcomeMastery from "./OutcomeMastery";

const UnapprovedOutcomeToggle = ({
  outcome,
  course,
  offering,
  refreshLOs,
  setNumUnapprovedOutcomes,
}) => {
  const showNotification = useNotification();
  const { setRefreshMetrics, setSubjects } = useContext(RefreshMetricsContext);
  const userDetails = useContext(UserDetailContext);
  const { setCourses, qcEnabled, qcConfig } = useContext(OutcomesContext);
  const [expanded, setExpanded] = useState(false);
  const [tokenLevels, setTokenLevels] = useState([]);
  const [deleteOutcomeOpen, setDeleteOutcomeOpen] = useState(false);
  const [aiOutcomeStatementsPending, setAiOutcomeStatementsPending] =
    useState(false);
  const [aiOutcomeResponse, setAiOutcomeResponse] = useState(null);
  const [selectedClassification, setSelectedClassification] = useState(null);
  const [relatedOutcomeId, setRelatedOutcomeId] = useState(null);
  const [effectiveTerm, setEffectiveTerm] = useState(null);
  const [classifying, setClassifying] = useState(false);

  useEffect(() => {
    const tokens = [];
    outcome.taxonomy_tokens.forEach((taxonomy_token) => {
      taxonomy_token.matched_tokens.forEach((token) => {
        tokens.push(token.origin_token);
      });
    });
    setTokenLevels(tokens);
  }, [outcome]);

  const handleDeleteOutcome = async () => {
    await LearningOutcomesService.deleteLearningOutcome(outcome, course.id)
      .then(() => {
        setCourses((prevCourses) => {
          const newCourses = prevCourses.map((c) => {
            if (c.id === course.id) {
              const newOfferings = c.offerings.map((off) => {
                if (off.id === offering.id) {
                  off.learning_outcomes = off.learning_outcomes.filter(
                    (lo) => lo.id !== outcome.id
                  );
                }
                return off;
              });
              c.offerings = newOfferings;
            }
            return c;
          });
          return newCourses;
        });
        setSubjects((prevSubjects) =>
          prevSubjects.map((subject) => {
            if (subject.title === course.academic_subject.title) {
              return {
                ...subject,
                unapproved_outcome_count: subject.unapproved_outcome_count - 1,
                renderAfterLabel: (
                  <Text size="small" fontStyle="italic">{`${
                    subject.unapproved_outcome_count - 1
                  } ${t("analyze.unapproved")}`}</Text>
                ),
              };
            }
            return subject;
          })
        );

        setNumUnapprovedOutcomes((prev) => prev - 1);
        setRefreshMetrics(true);
        setDeleteOutcomeOpen(false);
        showNotification(
          t("notifications.general.deleteSuccess", { obj: "Outcome" })
        );
      })
      .catch(() => {
        showNotification(
          t("notifications.general.deleteError", { obj: "Outcome" }),
          ERROR
        );
      });
  };

  const handleAiButton = () => {
    setAiOutcomeStatementsPending(true);
    setExpanded(true);
    AIService.getEnhancedOutcomeStatement(
      outcome.id,
      offering.course_summary.parent_course_id,
      offering.course_summary.representative_section_name,
      offering.code
    )
      .then((outcomeResponse) => setAiOutcomeResponse(outcomeResponse))
      .catch(() => {
        setAiOutcomeResponse({ error: t("analyze.aiError") });
      })
      .finally(() => setAiOutcomeStatementsPending(false));
  };

  const handleApprove = (outcomeId, offering) => {
    setClassifying(true);
    let earliestCanvasTerm = null;
    if (effectiveTerm) {
      earliestCanvasTerm = getEarliestCanvasTerm(effectiveTerm);
    }
    LearningOutcomesService.classifyOutcome({
      outcomeId: outcomeId,
      contextId: offering.course_summary.parent_course_id,
      loClass: selectedClassification,
      relatedOutcomeId: relatedOutcomeId ?? null,
      userId: userDetails.canvas_id,
      effectiveTermId: earliestCanvasTerm?.id ?? undefined,
    })
      .then(async () => {
        await refreshLOs(qcEnabled);
        setSubjects((prevSubjects) =>
          prevSubjects.map((subject) => {
            if (subject.title === course.academic_subject.title) {
              return {
                ...subject,
                unapproved_outcome_count: subject.unapproved_outcome_count - 1,
                renderAfterLabel: (
                  <Text size="small" fontStyle="italic">{`${
                    subject.unapproved_outcome_count - 1
                  } ${t("analyze.unapproved")}`}</Text>
                ),
              };
            }
            return subject;
          })
        );
        setNumUnapprovedOutcomes((prev) => prev - 1);
        setRefreshMetrics(true);
      })
      .finally(() => setClassifying(false));
  };

  function validOutcome(lo) {
    // Exclude its own outcome, unapproved outcomes, and non-CSLOs
    if (lo.id === outcome.id || lo.unapproved || lo.outcome_type !== "CSLO") {
      return false;
    }
    // For versioning, only allow outcomes that are not archived, not pending archive, and have no future versions
    if (selectedClassification === "version") {
      return (
        !isArchived(lo) &&
        !pendingArchive(lo) &&
        getFutureVersions(lo).length === 0
      );
    }
    // For variant, allow any outcome that does not fit the first set of criteria
    if (selectedClassification === "variant") {
      return true;
    }
    return false;
  }

  return (
    <div
      style={{
        border: "1px solid #ccc",
        marginBottom: 15,
        borderRadius: "5px",
      }}
    >
      <ToggleGroup
        size="small"
        border={false}
        iconPosition="end"
        fluidWidth
        toggleLabel={`${t("analyze.showingResultsFor")} ${offering.code}`}
        expanded={expanded}
        onToggle={(_e, exp) => {
          setExpanded(exp);
          setSelectedClassification(null);
          setRelatedOutcomeId(null);
          setEffectiveTerm(null);
        }}
        summary={
          <Flex gap="small">
            <Flex.Item shouldGrow shouldShrink>
              <HighlightTokens
                title={outcome.name}
                text={outcome.statement}
                tokenLevels={tokenLevels}
                qcEnabled={qcEnabled}
              />
            </Flex.Item>
            <Flex.Item>
              <Pill color="warning">{t("analyze.unapproved")}</Pill>
            </Flex.Item>
            <Flex.Item>
              <IconButton
                screenReaderLabel={t("learningOutcomes.deleteOutcome")}
                renderIcon={IconTrashLine}
                size="small"
                onClick={() => setDeleteOutcomeOpen(true)}
              />
            </Flex.Item>
            {qcEnabled && qcConfig.ai_enabled && (
              <Flex.Item>
                <AiStatementButton
                  handler={handleAiButton}
                  loading={aiOutcomeStatementsPending}
                />
              </Flex.Item>
            )}
            {outcome.warnings && outcome.warnings.length > 0 && qcEnabled && (
              <Flex.Item>
                <IconWarningSolid size="x-small" color="warning" />
              </Flex.Item>
            )}
          </Flex>
        }
      >
        <div style={{ padding: "10px 15px" }}>
          <Grid>
            <Grid.Row>
              <Grid.Col>
                {qcEnabled &&
                  qcConfig.ai_enabled &&
                  (aiOutcomeStatementsPending ? (
                    <Skeleton count={1} height={46} />
                  ) : (
                    !!aiOutcomeResponse &&
                    (aiOutcomeResponse.error ? (
                      <Alert
                        variant="warning"
                        timeout={5000}
                        onDismiss={() => setAiOutcomeResponse(null)}
                      >
                        {aiOutcomeResponse.error}
                      </Alert>
                    ) : (
                      <AiStatement statement={aiOutcomeResponse} />
                    ))
                  ))}
                {qcEnabled &&
                  outcome.warnings &&
                  outcome.warnings.length > 0 && (
                    <Alert variant="warning">
                      <ul>
                        {outcome.warnings.map((warning, key) => (
                          <li key={key}>{warning}</li>
                        ))}
                      </ul>
                    </Alert>
                  )}
                <Alert variant="info">
                  {t("analyze.approvalDescriptionAc")}
                </Alert>
              </Grid.Col>
            </Grid.Row>
            <Grid.Row>
              <Grid.Col>
                <OutcomeMastery outcome={outcome} />
                <br />
                <Text>{t("analyze.rubricDetailsUnapproved")}</Text>
              </Grid.Col>
            </Grid.Row>
            <Grid.Row>
              <Grid.Col width={4} vAlign="middle">
                <ClassSelect
                  selectHandler={(classification) =>
                    setSelectedClassification(classification)
                  }
                  selectedItemId={selectedClassification}
                />
              </Grid.Col>
            </Grid.Row>
            {(selectedClassification === "variant" ||
              selectedClassification === "version") && (
              <Grid.Row>
                <Grid.Col>
                  {offering.learning_outcomes.filter((lo) => validOutcome(lo))
                    .length > 0 ? (
                    <RadioInputGroup
                      name={"outcomeRadio" + outcome.id}
                      description={t("review.selectOutcome") + ":"}
                      value={relatedOutcomeId ?? null}
                      onChange={() => {}}
                    >
                      {sortOutcomes(offering.learning_outcomes)
                        .filter((lo) => validOutcome(lo))
                        .map((lo) => {
                          const currentOutcome = getCurrentVersion(lo);
                          return (
                            <RadioInput
                              onClick={() =>
                                setRelatedOutcomeId(currentOutcome.id)
                              }
                              label={
                                currentOutcome.statement ? (
                                  <Text size="small">
                                    <strong>{currentOutcome.name}</strong>
                                    {<br />}
                                    {currentOutcome.statement}
                                  </Text>
                                ) : (
                                  <Text>
                                    {" "}
                                    <strong>{currentOutcome.name}</strong>
                                  </Text>
                                )
                              }
                              key={outcome.id + "-" + currentOutcome.id}
                              value={currentOutcome.id}
                            />
                          );
                        })}
                    </RadioInputGroup>
                  ) : (
                    <Alert>{t("analyze.noOutcomesApproved")}</Alert>
                  )}
                </Grid.Col>
              </Grid.Row>
            )}
            <Grid.Row>
              <Grid.Col>
                <Flex
                  justifyItems="space-between"
                  alignItems="end"
                  direction="row-reverse"
                >
                  <Flex.Item>
                    <Button
                      margin="0 small"
                      color="primary"
                      interaction={
                        !classifying &&
                        (selectedClassification === "new" ||
                          selectedClassification === "private" ||
                          (relatedOutcomeId &&
                            (selectedClassification === "version" ||
                              selectedClassification === "variant")))
                          ? "enabled"
                          : "disabled"
                      }
                      onClick={() => handleApprove(outcome.id, offering)}
                    >
                      {classifying ? (
                        <Spinner
                          renderTitle={t("global.pleaseWait")}
                          size="x-small"
                        />
                      ) : selectedClassification !== "version" ? (
                        t("analyze.approve")
                      ) : (
                        t(
                          "analyze.approve" // restore "analyze.approveAndMapScale" when ready to reinforce that workflow
                        )
                      )}
                    </Button>
                  </Flex.Item>
                  {offering.learning_outcomes.filter((lo) => validOutcome(lo))
                    .length > 0 &&
                    selectedClassification === "version" && (
                      <Flex.Item>
                        <EffectiveTermSelect
                          selectHandler={(term) => setEffectiveTerm(term)}
                          selectedTermId={effectiveTerm?.id}
                        />
                      </Flex.Item>
                    )}
                </Flex>
              </Grid.Col>
            </Grid.Row>
          </Grid>
        </div>
      </ToggleGroup>
      <DialogYesNo
        message={t("review.deleteOutcomeConfirm")}
        openStatus={deleteOutcomeOpen}
        onClickNo={() => setDeleteOutcomeOpen(false)}
        onClickYes={handleDeleteOutcome}
      />
    </div>
  );
};

export default UnapprovedOutcomeToggle;
